import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Plus, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { workerProfileApi, portfoliosApi, categoriesApi, API_URL } from '../../services/api';
import { SecureImage } from '../../components/ui/SecureImage';
import { Skeleton } from '../../components/ui/Skeleton';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { StepScaffold } from '../../components/obros/StepScaffold';
import type { WorkerProfile, Category, Portfolio, Skill } from '../../types';

const DAYS = [
  { value: 'mon', label: 'Lun' },
  { value: 'tue', label: 'Mar' },
  { value: 'wed', label: 'Mié' },
  { value: 'thu', label: 'Jue' },
  { value: 'fri', label: 'Vie' },
  { value: 'sat', label: 'Sáb' },
  { value: 'sun', label: 'Dom' },
];

const MISSING_FIELD_LABELS: Record<string, string> = {
  avatar: 'Foto de perfil',
  city: 'Localidad',
  bio: 'Descripción profesional',
  dni: 'DNI',
  selfie_path: 'Selfie',
  professional_title: 'Título profesional',
  years_experience: 'Años de experiencia',
  coverage_radius_km: 'Zona de cobertura',
  available_days: 'Disponibilidad',
  categories: 'Oficio principal',
  skills: 'Especialidades',
  reference: 'Referencia (nombre y teléfono)',
  terms_accepted_at: 'Aceptación de términos',
  portfolio_photos: 'Al menos 3 fotos de trabajos realizados',
};

const statusMeta: Record<WorkerProfile['profile_status'], { label: string; className: string }> = {
  draft: { label: 'Borrador', className: 'bg-secondary-100 text-secondary-600' },
  pending_review: { label: 'En revisión', className: 'bg-warning-100 text-warning-700' },
  needs_correction: { label: 'Requiere correcciones', className: 'bg-danger-100 text-danger-700' },
  verified: { label: 'Verificado', className: 'bg-success-100 text-success-700' },
  rejected: { label: 'Rechazado', className: 'bg-danger-100 text-danger-700' },
};

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
      active ? 'bg-primary-500 text-white border-primary-500' : 'bg-surface border-outlineVarWarm text-onSurface hover:border-primary-300'
    }`}
  >
    {children}
  </button>
);

const TOTAL_STEPS = 7;

// Calca app/obros/lib/screens/worker_onboarding/* (wizard de 7 pasos).
export const ProfessionalProfile: React.FC = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuthStore();
  const [profile, setProfile] = useState<WorkerProfile | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState<string[] | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    dni: '',
    professional_title: '',
    years_experience: '',
    coverage_radius_km: '',
    available_days: [] as string[],
    available_hours: '',
    license_type: '',
    license_number: '',
    license_issuer: '',
    license_expires_at: '',
    reference_name: '',
    reference_phone: '',
    reference_relation: '',
  });

  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

  const [newPortfolio, setNewPortfolio] = useState<{ title: string; description: string; images: File[]; authorized: boolean }>({
    title: '',
    description: '',
    images: [],
    authorized: false,
  });
  const [portfolioSaving, setPortfolioSaving] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([workerProfileApi.show(), portfoliosApi.mine(), categoriesApi.list({ with_count: 1 })])
      .then(([profileRes, portfoliosRes, categoriesRes]) => {
        const p: WorkerProfile = profileRes.data.profile;
        setProfile(p);
        setSelectedSkillIds((profileRes.data.skills || []).map((s: Skill) => s.id));
        setSelectedCategoryIds((profileRes.data.categories || []).map((c: Category) => c.id));
        setPortfolios(portfoliosRes.data);
        setCategories(categoriesRes.data);
        setForm({
          dni: p.dni || '',
          professional_title: p.professional_title || '',
          years_experience: p.years_experience?.toString() || '',
          coverage_radius_km: p.coverage_radius_km?.toString() || '',
          available_days: p.available_days || [],
          available_hours: p.available_hours || '',
          license_type: p.license_type || '',
          license_number: p.license_number || '',
          license_issuer: p.license_issuer || '',
          license_expires_at: p.license_expires_at?.slice(0, 10) || '',
          reference_name: p.reference_name || '',
          reference_phone: p.reference_phone || '',
          reference_relation: p.reference_relation || '',
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const toggleDay = (day: string) => {
    setForm((f) => ({
      ...f,
      available_days: f.available_days.includes(day) ? f.available_days.filter((d) => d !== day) : [...f.available_days, day],
    }));
  };

  const toggleSkill = (id: number) => {
    setSelectedSkillIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const toggleCategory = (id: number) => {
    setSelectedCategoryIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await workerProfileApi.update({
        ...form,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        coverage_radius_km: form.coverage_radius_km ? Number(form.coverage_radius_km) : null,
      });
      setProfile(res.data.profile);
      await workerProfileApi.updateCategories(selectedCategoryIds);
      await workerProfileApi.updateSkills(selectedSkillIds);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (type: 'dni_front' | 'dni_back' | 'selfie' | 'license_document', file: File) => {
    const res = await workerProfileApi.uploadDocument(type, file);
    setProfile(res.data.profile);
  };

  const handleAcceptTerms = async () => {
    const res = await workerProfileApi.acceptTerms();
    setProfile(res.data.profile);
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolio.title || newPortfolio.images.length === 0 || !newPortfolio.authorized) return;
    setPortfolioSaving(true);
    try {
      await portfoliosApi.create({
        title: newPortfolio.title,
        description: newPortfolio.description || undefined,
        images: newPortfolio.images,
        authorized_publish: newPortfolio.authorized,
      });
      setNewPortfolio({ title: '', description: '', images: [], authorized: false });
      const res = await portfoliosApi.mine();
      setPortfolios(res.data);
    } finally {
      setPortfolioSaving(false);
    }
  };

  const handleDeletePortfolio = async (id: number) => {
    if (!window.confirm('¿Eliminar este trabajo del portfolio?')) return;
    await portfoliosApi.delete(id);
    setPortfolios((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSubmit = async () => {
    setSubmitMessage(null);
    setMissing(null);
    try {
      const res = await workerProfileApi.submit();
      setProfile(res.data.profile);
      setSubmitMessage(res.data.message);
      fetchUser();
    } catch (err: any) {
      if (err.response?.status === 422) {
        setMissing(err.response.data.missing_fields || []);
      }
    }
  };

  const availableSkills: Skill[] = (() => {
    const skillsById = new Map<number, Skill>();
    categories.filter((c) => selectedCategoryIds.includes(c.id)).forEach((c) => (c.skills || []).forEach((s) => skillsById.set(s.id, s)));
    return Array.from(skillsById.values());
  })();

  const totalPortfolioImages = portfolios.reduce((n, p) => n + (p.images?.length || 0), 0);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (submitMessage) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <CheckCircle2 className="w-14 h-14 text-success-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-onSurface mb-2">¡Listo!</h1>
        <p className="text-onSurface-mute mb-6">{submitMessage}</p>
        <Button variant="filled" pill size="xl" className="w-full" onClick={() => navigate('/profile')}>
          Ir a mi perfil
        </Button>
      </div>
    );
  }

  const goNext = async (afterSave?: () => Promise<void>) => {
    if (afterSave) await afterSave();
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const stepMeta: Record<number, { title: string; subtitle?: string; onContinue?: () => void; continueLabel?: string; continueDisabled?: boolean }> = {
    1: { title: 'Tu perfil público', subtitle: 'Así te van a ver los clientes', onContinue: () => goNext(handleSaveProfile) },
    2: { title: 'Oficio y especialidades', subtitle: 'Elegí en qué sos bueno', onContinue: () => goNext(handleSaveProfile) },
    3: {
      title: 'Portfolio',
      subtitle: 'Mostrá trabajos que ya hiciste',
      onContinue: () => goNext(),
      continueLabel: totalPortfolioImages >= 3 ? 'Continuar' : `Faltan ${3 - totalPortfolioImages} fotos`,
      continueDisabled: totalPortfolioImages < 3,
    },
    4: { title: 'Matrícula', subtitle: 'Opcional, pero ayuda a generar confianza', onContinue: () => goNext(handleSaveProfile) },
    5: {
      title: 'Referencia',
      subtitle: 'Alguien que pueda dar fe de tu trabajo',
      onContinue: () => goNext(handleSaveProfile),
      continueDisabled: !form.reference_name.trim() || !form.reference_phone.trim(),
    },
    6: { title: 'Identidad', subtitle: 'Solo lo ve el equipo de moderación', onContinue: () => goNext() },
    7: { title: 'Términos y envío', subtitle: 'Último paso', onContinue: handleSubmit, continueLabel: 'Enviar a revisión' },
  };

  const current = stepMeta[step];

  return (
    <div className="max-w-2xl mx-auto">
      {profile && (
        <div className="px-4 pt-4 flex items-center gap-3 flex-wrap">
          <span className={`badge ${statusMeta[profile.profile_status].className}`}>{statusMeta[profile.profile_status].label}</span>
          {profile.profile_status === 'needs_correction' && profile.moderation_notes && (
            <p className="text-sm text-danger-700">{profile.moderation_notes}</p>
          )}
        </div>
      )}

      <StepScaffold
        title={current.title}
        subtitle={current.subtitle}
        step={step}
        totalSteps={TOTAL_STEPS}
        onBack={step > 1 ? () => setStep((s) => s - 1) : undefined}
        onContinue={current.onContinue}
        continueLabel={current.continueLabel}
        continueDisabled={current.continueDisabled}
        isLoading={saving}
      >
        {step === 1 && (
          <div className="space-y-4">
            <Input pill label="Título profesional" value={form.professional_title} onChange={(e) => setForm((f) => ({ ...f, professional_title: e.target.value }))} placeholder="Electricista domiciliario" />
            <div className="grid grid-cols-2 gap-4">
              <Input pill type="number" min={0} label="Años de experiencia" value={form.years_experience} onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))} />
              <Input pill type="number" min={0} label="Radio de cobertura (km)" value={form.coverage_radius_km} onChange={(e) => setForm((f) => ({ ...f, coverage_radius_km: e.target.value }))} />
            </div>
            <Input pill label="Horario habitual" value={form.available_hours} onChange={(e) => setForm((f) => ({ ...f, available_hours: e.target.value }))} placeholder="9 a 18hs" />
            <div>
              <p className="text-sm font-bold text-onSurface mb-2">Días disponibles</p>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <Chip key={d.value} active={form.available_days.includes(d.value)} onClick={() => toggleDay(d.value)}>
                    {d.label}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm font-bold text-onSurface mb-2">Oficios (podés elegir más de uno)</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((c) => (
                <Chip key={c.id} active={selectedCategoryIds.includes(c.id)} onClick={() => toggleCategory(c.id)}>
                  {c.name}
                </Chip>
              ))}
            </div>
            <p className="text-sm font-bold text-onSurface mb-2">Especialidades</p>
            {selectedCategoryIds.length === 0 ? (
              <p className="text-onSurface-mute text-sm">Elegí primero al menos un oficio.</p>
            ) : availableSkills.length === 0 ? (
              <p className="text-onSurface-mute text-sm">Estas categorías no tienen especialidades cargadas.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableSkills.map((s) => (
                  <Chip key={s.id} active={selectedSkillIds.includes(s.id)} onClick={() => toggleSkill(s.id)}>
                    {s.name}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {portfolios.map((p) => (
                <div key={p.id} className="border border-outlineVarWarm rounded-2xl p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-onSurface text-sm">{p.title}</h3>
                    <button onClick={() => handleDeletePortfolio(p.id)} className="text-danger-500 hover:text-danger-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {(p.images || []).map((img, i) => (
                      <img key={i} src={`${API_URL.replace(/\/api\/?$/, '')}/storage/${img}`} alt={p.title} className="w-14 h-14 object-cover rounded-lg border border-outlineVarWarm" />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-dashed border-outlineWarm rounded-2xl p-4 space-y-3">
              <h3 className="font-medium text-onSurface text-sm">Agregar trabajo</h3>
              <Input pill placeholder="Título del trabajo" value={newPortfolio.title} onChange={(e) => setNewPortfolio((p) => ({ ...p, title: e.target.value }))} />
              <textarea
                className="w-full rounded-2xl border border-outlineVarWarm bg-surface-dim p-3 text-onSurface placeholder-onSurface-mute focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                placeholder="Descripción (opcional)"
                rows={2}
                value={newPortfolio.description}
                onChange={(e) => setNewPortfolio((p) => ({ ...p, description: e.target.value }))}
              />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setNewPortfolio((p) => ({ ...p, images: Array.from(e.target.files || []) }))}
                className="text-sm text-onSurface-mute"
              />
              <label className="flex items-start gap-2 text-sm text-onSurface-var">
                <input type="checkbox" checked={newPortfolio.authorized} onChange={(e) => setNewPortfolio((p) => ({ ...p, authorized: e.target.checked }))} className="mt-1" />
                Declaro que las imágenes corresponden a trabajos realizados por mí o mi equipo y que tengo autorización para publicarlas.
              </label>
              <Button
                variant="tonal"
                onClick={handleCreatePortfolio}
                disabled={portfolioSaving || !newPortfolio.title || newPortfolio.images.length === 0 || !newPortfolio.authorized}
                isLoading={portfolioSaving}
              >
                <Plus className="w-4 h-4" /> Agregar
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="rounded-xl bg-cream text-sm text-onSurface-var p-3 mb-4">No es obligatorio, pero aumenta la confianza de los clientes.</div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Input pill placeholder="Tipo (ej. matrícula de gasista)" value={form.license_type} onChange={(e) => setForm((f) => ({ ...f, license_type: e.target.value }))} />
              <Input pill placeholder="Número" value={form.license_number} onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))} />
              <Input pill placeholder="Organismo emisor" value={form.license_issuer} onChange={(e) => setForm((f) => ({ ...f, license_issuer: e.target.value }))} />
              <Input pill type="date" value={form.license_expires_at} onChange={(e) => setForm((f) => ({ ...f, license_expires_at: e.target.value }))} />
            </div>
            <DocumentUpload label="Documento de la matrícula" type="license_document" path={profile?.license_document_path} onUpload={(file) => handleUploadDocument('license_document', file)} />
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <Input pill required label="Nombre" value={form.reference_name} onChange={(e) => setForm((f) => ({ ...f, reference_name: e.target.value }))} />
            <Input pill required label="Teléfono" value={form.reference_phone} onChange={(e) => setForm((f) => ({ ...f, reference_phone: e.target.value }))} />
            <Input pill label="Relación (ej. cliente anterior)" value={form.reference_relation} onChange={(e) => setForm((f) => ({ ...f, reference_relation: e.target.value }))} />
          </div>
        )}

        {step === 6 && (
          <div>
            <Input pill label="DNI" value={form.dni} onChange={(e) => setForm((f) => ({ ...f, dni: e.target.value }))} placeholder="30123456" className="mb-4" />
            <div className="grid grid-cols-3 gap-3">
              <DocumentUpload label="Frente del DNI" type="dni_front" path={profile?.dni_front_path} onUpload={(file) => handleUploadDocument('dni_front', file)} />
              <DocumentUpload label="Dorso del DNI" type="dni_back" path={profile?.dni_back_path} onUpload={(file) => handleUploadDocument('dni_back', file)} />
              <DocumentUpload label="Selfie con DNI" type="selfie" path={profile?.selfie_path} onUpload={(file) => handleUploadDocument('selfie', file)} />
            </div>
            <p className="text-xs text-onSurface-mute mt-4">El DNI, la selfie y la documentación cargada nunca se muestran públicamente — solo los ve el equipo de moderación.</p>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-4">
            {missing && missing.length > 0 && (
              <div className="p-4 rounded-xl bg-warning-50 text-warning-800 text-sm">
                <div className="flex items-center gap-2 font-medium mb-2">
                  <AlertTriangle className="w-4 h-4" /> Todavía falta completar:
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {missing.map((m) => (
                    <li key={m}>{MISSING_FIELD_LABELS[m] || m}</li>
                  ))}
                </ul>
              </div>
            )}

            {profile?.terms_accepted_at ? (
              <p className="text-success-700 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Aceptaste los términos y el código de conducta.
              </p>
            ) : (
              <Button variant="tonal" className="w-full" onClick={handleAcceptTerms}>
                Acepto los términos, el código de conducta y el tratamiento de mis datos personales
              </Button>
            )}
          </div>
        )}
      </StepScaffold>
    </div>
  );
};

const DocumentUpload: React.FC<{
  label: string;
  type: 'dni_front' | 'dni_back' | 'selfie' | 'license_document';
  path?: string;
  onUpload: (file: File) => void;
}> = ({ label, type, path, onUpload }) => {
  const inputId = `doc-${type}`;
  return (
    <div>
      <p className="text-xs text-onSurface-mute mb-1.5">{label}</p>
      {path ? (
        <SecureImage path={workerProfileApi.documentPath(type)} alt={label} className="w-full h-28 object-cover rounded-xl border border-outlineVarWarm mb-2" />
      ) : (
        <div className="w-full h-28 rounded-xl border border-dashed border-outlineWarm flex items-center justify-center text-onSurface-mute mb-2">
          <Camera className="w-6 h-6" />
        </div>
      )}
      <label htmlFor={inputId} className="block w-full text-center cursor-pointer text-xs py-1.5 rounded-full bg-cream text-primary-700 font-medium">
        {path ? 'Reemplazar' : 'Subir'}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
        }}
      />
    </div>
  );
};
